<!-- eslint-disable perfectionist/sort-imports -->
<script setup lang="ts">
import { computed, ref, toRef } from 'vue';

import ConfirmModal from '~/components/shared/ConfirmModal.vue';
import { DATE_FORMATS } from '~/constants';
import usePermission from '~/hooks/usePermission';
import date from '~/utils/date';

import useDeleteInstallationBypass from '../queries/useDeleteInstallationBypass';
import useInstallationBypass from '../queries/useInstallationBypass';
import InstallationBypassModal from './InstallationBypassModal.vue';

const BYPASS_STEP_LABEL = 'Confirmação do Cliente';

const props = defineProps<{
  partnerId: number;
}>();

const partnerIdRef = toRef(() => props.partnerId);

const { hasAccess: canEdit } = usePermission(['installation_approvers:edit']);

const {
  data: bypassRecord,
  error: bypassError,
  isLoading,
} = useInstallationBypass(partnerIdRef);

const deleteMutation = useDeleteInstallationBypass();

const showBypassModal = ref(false);
const bypassModalMode = ref<'create' | 'edit'>('create');
const showRemoveConfirm = ref(false);

const hasActiveBypass = computed(() => bypassRecord.value !== null && bypassRecord.value !== undefined);

function closeBypassModal() {
  showBypassModal.value = false;
}

function formatBoolean(value?: boolean) {
  return value ? 'Sim' : 'Não';
}

function formatDateTime(value?: string) {
  return value ? date(value).format(DATE_FORMATS.DATETIME) : '—';
}

async function handleConfirmRemove() {
  if (!canEdit.value) return;

  try {
    await deleteMutation.mutateAsync({ partnerId: props.partnerId });
    showRemoveConfirm.value = false;
  } catch {
    // handled by mutation
  }
}

function handleRemoveCancel() {
  showRemoveConfirm.value = false;
}

function openCreateModal() {
  if (!canEdit.value || hasActiveBypass.value) return;
  bypassModalMode.value = 'create';
  showBypassModal.value = true;
}

function openEditModal() {
  if (!canEdit.value || !hasActiveBypass.value) return;
  bypassModalMode.value = 'edit';
  showBypassModal.value = true;
}

function openRemoveConfirm() {
  if (!canEdit.value || !hasActiveBypass.value) return;
  showRemoveConfirm.value = true;
}
</script>

<template>
  <div class="col-12 mb-3">
    <div class="card">
      <div class="card-header d-flex align-items-center justify-content-between">
        <h3 class="card-title mb-0">
          Bypass de Instalação
        </h3>
        <button
          v-if="!hasActiveBypass"
          v-permission="['installation_approvers:edit']"
          type="button"
          class="btn btn-sm btn-primary"
          :disabled="isLoading"
          @click="openCreateModal"
        >
          Adicionar
        </button>
      </div>

      <div class="card-body">
        <div v-if="isLoading">
          <div class="placeholder-glow">
            <div class="placeholder col-8 mb-2" />
            <div class="placeholder col-12" />
            <div class="placeholder col-6" />
          </div>
        </div>

        <div v-else-if="bypassError" class="alert alert-danger" role="alert">
          Erro ao carregar bypass de instalação do parceiro.
        </div>

        <div
          v-else-if="!hasActiveBypass"
          class="text-secondary"
        >
          Nenhum bypass ativo para este parceiro.
        </div>

        <div v-else class="row g-3">
          <div class="col-md-6">
            <div class="text-secondary small">
              Etapa
            </div>
            <div>{{ BYPASS_STEP_LABEL }}</div>
          </div>

          <div class="col-md-6">
            <div class="text-secondary small">
              Criado por
            </div>
            <div>{{ bypassRecord?.created_by || '—' }}</div>
          </div>

          <div class="col-md-6">
            <div class="text-secondary small">
              Pedidos Solfácil
            </div>
            <div>{{ formatBoolean(bypassRecord?.solfacil_orders) }}</div>
          </div>

          <div class="col-md-6">
            <div class="text-secondary small">
              Container
            </div>
            <div>{{ formatBoolean(bypassRecord?.containers) }}</div>
          </div>

          <div class="col-md-6">
            <div class="text-secondary small">
              Criado em
            </div>
            <div>{{ formatDateTime(bypassRecord?.created_at) }}</div>
          </div>

          <div class="col-12">
            <div class="text-secondary small">
              Motivo
            </div>
            <div class="text-break">
              {{ bypassRecord?.reason || '—' }}
            </div>
          </div>

          <div class="col-12 d-flex gap-2">
            <button
              v-permission="['installation_approvers:edit']"
              type="button"
              class="btn btn-sm btn-outline-primary"
              @click="openEditModal"
            >
              Editar
            </button>
            <button
              v-permission="['installation_approvers:edit']"
              type="button"
              class="btn btn-sm btn-outline-danger"
              :disabled="deleteMutation.isPending.value"
              @click="openRemoveConfirm"
            >
              Remover
            </button>
          </div>
        </div>
      </div>
    </div>

    <InstallationBypassModal
      v-model="showBypassModal"
      :mode="bypassModalMode"
      :initial-record="bypassRecord"
      :partner-id="partnerId"
      @close="closeBypassModal"
    />

    <ConfirmModal
      v-model="showRemoveConfirm"
      title="Remover bypass de instalação"
      message="Tem certeza que deseja remover o bypass de instalação deste parceiro?"
      type="danger"
      confirm-text="Remover"
      :loading="deleteMutation.isPending.value"
      @confirm="handleConfirmRemove"
      @cancel="handleRemoveCancel"
    />
  </div>
</template>
